import { Box, TableCell, TableRow, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BlockClicks } from "lib/@components/block-clicks"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { ContextMenuBar } from "lib/@components/context-menu-bar"
import { RegimeItemLeft, RegimeItemRight } from "slot-definitions"
import { navigate } from "lib/routes/navigate"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { RegimeRowThumbnail } from "./regime-row-thumbnail"
import { RegimeStateBadge } from "./regime-state-badge"
import { cleanRegimeName } from "./clean-regime-name"
import { RegimeTableExtraCell } from "./regime-table-extra-cell"
import { RegimeItemDescriptionText, regimeDescriptionId } from "./regime-item-description"

export function RegimeTableRow({ item }) {
    const { regimeTableColumns } = useBoundContext()
    const name = cleanRegimeName(item.label)
    const facilities = item.$?.facilities?.filter(Boolean) ?? []
    const facilitiesLabel = facilities.length ? facilities.join(", ") : "No facilities"
    const modified = item.$?.modified
    const modifiedLabel = modified ? new Date(modified).format(DATE_FORMATS.medium) : "—"

    return (
        <Bound parentItem={item}>
            <TableRow
                hover
                tabIndex={0}
                role="button"
                aria-label={`Open regime ${name}`}
                aria-describedby={regimeDescriptionId(item)}
                data-cy="regime-row"
                onClick={go}
                onKeyDown={handleRowKeyDown}
                sx={{
                    cursor: "pointer",
                    "&:focus-visible": {
                        backgroundColor: "action.focus",
                    },
                }}
            >
                <TableCell data-cy="regime-row-name">
                    <ListItemBox sx={{ gap: 1.5, minWidth: 0 }}>
                        <RegimeRowThumbnail item={item} />
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <TruncatedTypography variant="subtitle2" component="div">
                                {name}
                            </TruncatedTypography>
                            <Typography variant="caption" component="div" noWrap sx={{ color: "text.secondary" }}>
                                {facilitiesLabel}
                            </Typography>
                            <RegimeItemDescriptionText item={item} />
                        </Box>
                    </ListItemBox>
                </TableCell>
                <TableCell data-cy="regime-row-state">
                    <RegimeStateBadge item={item} />
                </TableCell>
                <Repeat list={regimeTableColumns} pass="column" keyFn="field" item={<RegimeTableExtraCell />} />
                <TableCell data-cy="regime-row-access">
                    <RegimeItemLeft.Slot regime={item} />
                </TableCell>
                <TableCell data-cy="regime-row-modified">
                    <Typography variant="body2" noWrap>
                        {modifiedLabel}
                    </Typography>
                </TableCell>
                <TableCell align="right" data-cy="regime-row-actions">
                    <BlockClicks>
                        <RegimeItemRight.Slot regime={item}>
                            <Secure demand="manageRegime" hide fallback={<Frag />} priority={1000}>
                                <ContextMenuBar type="regime" regime={item} />
                            </Secure>
                        </RegimeItemRight.Slot>
                    </BlockClicks>
                </TableCell>
            </TableRow>
        </Bound>
    )

    function go() {
        navigate(`/app/schedules?id=${item.id}`)
    }

    function handleRowKeyDown(e) {
        if (e.target !== e.currentTarget) return
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            go()
        }
    }
}
